import React, { useEffect, useRef, useState } from 'react'
import { drawLoom, exportLoomAsDataURL, exportLoomCard } from '../lib/loomRenderer'
import type { Thread } from '../lib/types'

interface LoomCanvasProps {
  threads: Thread[]
  title?: string
  onExport?: (dataUrl: string, kind: 'full' | 'card') => void
  className?: string
  interactive?: boolean // clicking a thread area can surface it for "re-weave"
  onThreadSelect?: (thread: Thread) => void
}

/**
 * React wrapper around the hand-crafted loom renderer.
 * Handles live animation when new threads arrive and export triggers.
 */
export const LoomCanvas: React.FC<LoomCanvasProps> = ({
  threads,
  title = 'Loom',
  onExport,
  className = '',
  interactive = false,
  onThreadSelect,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [lastNewId, setLastNewId] = useState<string | null>(null)
  const [isExporting, setIsExporting] = useState(false)

  // Draw whenever threads change. Drive a short "new arrival" animation.
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d', { alpha: false })
    if (!ctx) return

    // Fixed internal resolution for crisp weaving
    const displayW = 820
    const displayH = 460
    const dpr = window.devicePixelRatio || 1
    canvas.width = displayW * dpr
    canvas.height = displayH * dpr
    canvas.style.width = `${displayW}px`
    canvas.style.height = `${displayH}px`

    let raf = 0
    let start = Date.now()

    const render = () => {
      drawLoom(ctx, {
        width: displayW,
        height: displayH,
        threads,
        animateNew: !!lastNewId,
        newThreadId: lastNewId || undefined,
      })

      // Run animation frames for ~1.4s when a new thread just arrived
      if (lastNewId && Date.now() - start < 1450) {
        raf = requestAnimationFrame(render)
      } else if (lastNewId) {
        // Animation finished — clear the "new" marker so future draws are static
        setLastNewId(null)
      }
    }

    render()

    return () => cancelAnimationFrame(raf)
  }, [threads, lastNewId])

  // When parent tells us a brand new thread just landed, trigger the fly-in + beat animation
  useEffect(() => {
    if (threads.length > 0) {
      const newest = threads[threads.length - 1]
      if (newest.id !== lastNewId) {
        setLastNewId(newest.id)
      }
    }
  }, [threads])

  const handleExport = async (kind: 'full' | 'card') => {
    if (!threads.length) return
    setIsExporting(true)
    try {
      const dataUrl = kind === 'full'
        ? exportLoomAsDataURL(threads, title)
        : exportLoomCard(threads, title)
      onExport?.(dataUrl, kind)
    } finally {
      setIsExporting(false)
    }
  }

  // Very light interactivity: click roughly where a weft would be to "pull" it for spaced re-weave
  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!interactive || !onThreadSelect || threads.length === 0) return

    const rect = e.currentTarget.getBoundingClientRect()
    const y = ((e.clientY - rect.top) / rect.height) * 460

    // Approximate which thread row was clicked based on current ordering + spacing
    const approxIndex = Math.max(0, Math.min(threads.length - 1, Math.floor((y - 46) / 11.5)))
    const thread = threads[approxIndex]
    if (thread) onThreadSelect(thread)
  }

  return (
    <div className={`loom-canvas-wrap ${className}`}>
      <canvas
        ref={canvasRef}
        onClick={handleCanvasClick}
        className={interactive ? 'cursor-crosshair' : ''}
        aria-label="Woven loom visualization of status threat threads. Each horizontal weft is one anonymous contribution."
      />
      <div className="flex items-center justify-between mt-3 text-[12px] text-[#6b5c4a] px-1">
        <div>
          {threads.length} thread{threads.length === 1 ? '' : 's'} • handwoven metaphor
        </div>
        {onExport && (
          <div className="flex gap-2 no-print">
            <button
              onClick={() => handleExport('full')}
              disabled={isExporting || threads.length === 0}
              className="px-3 py-1 rounded border border-[#d4c8b5] hover:bg-white active:bg-[#f8f4ed] disabled:opacity-50 transition text-xs"
            >
              {isExporting ? 'Rendering…' : 'Export PNG'}
            </button>
            <button
              onClick={() => handleExport('card')}
              disabled={isExporting || threads.length === 0}
              className="px-3 py-1 rounded border border-[#d4c8b5] hover:bg-white active:bg-[#f8f4ed] disabled:opacity-50 transition text-xs"
            >
              Share card
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
